import React, { useState } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, Pause, Play, Plus, RefreshCw, Settings2 } from 'lucide-react';
import { pauseSubscription } from '../services/verseDeliveryService';
import { topicOptions } from '../data/verseDeliveryContent';
import useDeliveryState from '../hooks/useDeliveryState';

const cadenceLabels = {
  morning: 'Daily · 7:00 AM',
  nightly: 'Daily · 8:00 PM',
  weekdays: 'Weekdays · 8:00 AM',
  weekly: 'Sundays · 9:00 AM'
};

const channelLabels = { email: 'Email', slack: 'Slack' };

const topicLabel = (topic) => topicOptions.find((item) => item.id === topic)?.label || 'Scripture';

const whenLabel = (subscription) => {
  const base = cadenceLabels[subscription.cadence] || 'On your rhythm';
  if (!subscription.customTime) return base;
  const [hours, minutes] = subscription.customTime.split(':');
  const at = new Date();
  at.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(at);
  return `${base.split(' · ')[0]} · ${time}`;
};

export default function ScheduleManager() {
  const { subscriptions, loading, error, setState, reload } = useDeliveryState();
  const [busyId, setBusyId] = useState('');
  const [actionError, setActionError] = useState('');

  const togglePause = async (subscription) => {
    setBusyId(subscription.id);
    setActionError('');
    try {
      setState(await pauseSubscription(subscription.id, subscription.status === 'paused'));
    } catch (pauseError) {
      setActionError(pauseError.message || 'That schedule could not be updated.');
    } finally {
      setBusyId('');
    }
  };

  const hasEntries = subscriptions.length > 0;

  return (
    <div className="vd-shell vd-launcher-shell">
      <main className="vd-launcher vd-launcher-branch">
        <Link className="vd-launcher-back" to="/settings">
          <ChevronLeft size={17} aria-hidden="true" /> Settings
        </Link>
        <p className="vd-launcher-eyebrow">Optional verse delivery</p>

        {loading ? (
          <p className="vd-launcher-glimpse-state" role="status">Loading your schedule…</p>
        ) : error ? (
          <p className="vd-launcher-glimpse-state" role="alert">
            {error}
            <button type="button" onClick={() => reload().catch(() => undefined)}>
              <RefreshCw size={15} aria-hidden="true" /> Try again
            </button>
          </p>
        ) : (
          <>
            <h1>Your verse schedules</h1>
            <p className="vd-launcher-sub">
              {hasEntries
                ? 'Review your verse schedules, pause them, or adjust upcoming deliveries.'
                : 'Receive a verse by email when it suits you. Daily prayer, Scripture, and devotionals are always ready in the app.'}
            </p>

            {actionError ? <p className="vd-flow-error" role="alert">{actionError}</p> : null}

            {hasEntries ? (
              <ul className="vd-schedule-list">
                {subscriptions.map((subscription) => {
                  const paused = subscription.status === 'paused';
                  return (
                    <li className="vd-schedule-row" key={subscription.id}>
                      <div className="vd-schedule-row-top">
                        <span className={`vd-schedule-dot${paused ? ' is-paused' : ''}`} aria-hidden="true" />
                        <h2>{topicLabel(subscription.topic)}</h2>
                        <span className="vd-schedule-when">
                          {whenLabel(subscription)}{paused ? ' · paused' : ''}
                        </span>
                      </div>
                      <div className="vd-schedule-chips">
                        <span className="vd-chip is-plum">{topicLabel(subscription.topic)}</span>
                        <span className="vd-chip">{channelLabels[subscription.medium] || subscription.medium}</span>
                        {subscription.destination ? <span className="vd-chip">{subscription.destination}</span> : null}
                      </div>
                      <div className="vd-schedule-row-actions">
                        <button type="button" onClick={() => togglePause(subscription)} disabled={busyId === subscription.id}>
                          {paused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
                          {paused ? 'Resume' : 'Pause'}
                        </button>
                        <Link to="/practice/deliveries">
                          <Settings2 size={16} aria-hidden="true" /> Adjust deliveries
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="vd-schedule-empty">
                <strong>No deliveries yet</strong>
                <span>Choose a theme, frequency, and time whenever you are ready.</span>
              </div>
            )}

            <Link className="vd-schedule-new" to="/schedule?type=verse">
              <Plus size={19} aria-hidden="true" /> Add a verse schedule
            </Link>
            <p><Link to="/">Return to today’s readings</Link></p>
          </>
        )}
      </main>
    </div>
  );
}
